import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getAllEnterpriseGroups, GroupResponse } from '../logic/groupCreation/groupApi';
import { getGroupOverview, GroupOverviewResponse } from '../logic/groups/groupOverviewApi';
import { getGroupDassOverview, GroupDassOverviewResponse } from '../logic/groups/groupDassOverviewApi';
import { getComprehensiveEnterpriseOverview } from '../logic/enterpriseConsolidationApi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import StarRadarChart from '../components/StarRadarChart';

interface SowbAttribute {
  id: string;
  name: string;
  score: number;
  trend: string;
  status: 'Good' | 'Fair' | 'Poor';
  description: string;
  icon: string;
}

type GroupCategory = 'all' | 'municipality' | 'barangay' | 'manual';

// Helper function to determine group type and location from breadcrumbs
const getGroupCategory = (group: GroupResponse): { category: GroupCategory; municipality?: string; barangay?: string } => {
  const breadcrumbs = group.breadcrumbs || [];
  
  if (breadcrumbs.length === 0) {
    return { category: 'manual' };
  }
  
  // Check if it's in the demographic folder structure
  if (breadcrumbs[0] === 'Cities') {
    // Format: ['Cities', 'Region', 'City', ...] or ['Cities', 'Region', 'City', 'Barangay', ...]
    if (breadcrumbs.length >= 3) {
      // Has at least City level
      const municipality = breadcrumbs[2]; // City is at index 2
      
      if (breadcrumbs.length >= 4) {
        // Has Barangay level
        const barangay = breadcrumbs[3];
        return { category: 'barangay', municipality, barangay };
      } else {
        // Only up to City level
        return { category: 'municipality', municipality };
      }
    }
  }
  
  return { category: 'manual' };
};

const EnterpriseOverviewPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [isPdfExporting, setIsPdfExporting] = useState(false);

  const handleExportPDF = async () => {
    const el = pdfContentRef.current;
    if (!el) return;
    try {
      setIsPdfExporting(true);
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
        scrollY: -window.scrollY,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableW = pageW - margin * 2;
      const imgW = canvas.width;
      const imgH = canvas.height;
      const ratio = usableW / (imgW / (96 / 25.4));
      const scaledH = (imgH / (96 / 25.4)) * ratio;
      const pageContentH = pageH - margin * 2;
      let y = 0;
      let page = 0;
      while (y < scaledH) {
        if (page > 0) pdf.addPage();
        pdf.addImage(
          imgData,
          'PNG',
          margin,
          margin - y,
          usableW,
          scaledH
        );
        // Clip by adding a white rectangle to hide overflow at the bottom
        pdf.setFillColor(isDarkMode ? 15 : 248, isDarkMode ? 23 : 250, isDarkMode ? 42 : 252);
        pdf.rect(0, pageH - margin + 0.1, pageW, margin + 1, 'F');
        y += pageContentH;
        page++;
      }
      const groupLabel = filteredGroups.find(g => g.id === selectedGroupId)?.groupName || 'All Groups';
      const dateStr = new Date().toISOString().split('T')[0];
      pdf.save(`enterprise-overview-${groupLabel.replace(/[^a-z0-9]/gi, '_')}-${dateStr}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setIsPdfExporting(false);
    }
  };

  const ENTERPRISE_GROUP_STORAGE_KEY = 'enterpriseOverviewSelectedGroupId';
  const ENTERPRISE_ASSESSMENT_MODE_STORAGE_KEY = 'enterpriseOverviewAssessmentMode';

  const [enterpriseGroups, setEnterpriseGroups] = useState<GroupResponse[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>('all');
  const [selectedGroupCategory, setSelectedGroupCategory] = useState<GroupCategory>('all');
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>('all');
  const [selectedBarangay, setSelectedBarangay] = useState<string>('all');

  const [assessmentMode, setAssessmentMode] = useState<'dass21' | 'overall' | 'sowb'>(() => {
    const saved = localStorage.getItem(ENTERPRISE_ASSESSMENT_MODE_STORAGE_KEY);
    return (saved as 'dass21' | 'overall' | 'sowb') || 'dass21';
  });

  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [municipalityDropdownOpen, setMunicipalityDropdownOpen] = useState(false);
  const [barangayDropdownOpen, setBarangayDropdownOpen] = useState(false);
  const [assessmentDropdownOpen, setAssessmentDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | 'all'>(new Date().getFullYear());

  const [overview, setOverview] = useState<GroupOverviewResponse['data'] | null>(null);
  const [dassOverview, setDassOverview] = useState<GroupDassOverviewResponse['data'] | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const groupSelector = document.querySelector('.consultant-overview__group-selector');
      const categorySelector = document.querySelector('.consultant-overview__category-selector');
      const municipalitySelector = document.querySelector('.consultant-overview__municipality-selector');
      const barangaySelector = document.querySelector('.consultant-overview__barangay-selector');
      const assessmentSelector = document.querySelector('.consultant-overview__assessment-mode-selector');
      const yearSelector = document.querySelector('.consultant-overview__year-selector');
      
      if (groupSelector && !groupSelector.contains(target)) {
        setGroupDropdownOpen(false);
      }
      if (categorySelector && !categorySelector.contains(target)) {
        setCategoryDropdownOpen(false);
      }
      if (municipalitySelector && !municipalitySelector.contains(target)) {
        setMunicipalityDropdownOpen(false);
      }
      if (barangaySelector && !barangaySelector.contains(target)) {
        setBarangayDropdownOpen(false);
      }
      if (assessmentSelector && !assessmentSelector.contains(target)) {
        setAssessmentDropdownOpen(false);
      }
      if (yearSelector && !yearSelector.contains(target)) {
        setYearDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Persist assessment mode to localStorage
  useEffect(() => {
    localStorage.setItem(ENTERPRISE_ASSESSMENT_MODE_STORAGE_KEY, assessmentMode);
  }, [assessmentMode]);

  // Get unique municipalities from groups
  const availableMunicipalities = useMemo(() => {
    const municipalities = new Set<string>();
    enterpriseGroups.forEach(group => {
      if (group.id !== 'all') {
        const { category, municipality } = getGroupCategory(group);
        if ((selectedGroupCategory === 'all' || category === selectedGroupCategory) && municipality) {
          municipalities.add(municipality);
        }
      }
    });
    return Array.from(municipalities).sort();
  }, [enterpriseGroups, selectedGroupCategory]);

  // Get unique barangays from groups based on selected municipality
  const availableBarangays = useMemo(() => {
    const barangays = new Set<string>();
    enterpriseGroups.forEach(group => {
      if (group.id !== 'all') {
        const { category, municipality, barangay } = getGroupCategory(group);
        if (
          category === 'barangay' &&
          (selectedGroupCategory === 'all' || category === selectedGroupCategory) &&
          (selectedMunicipality === 'all' || municipality === selectedMunicipality) &&
          barangay
        ) {
          barangays.add(barangay);
        }
      }
    });
    return Array.from(barangays).sort();
  }, [enterpriseGroups, selectedGroupCategory, selectedMunicipality]);

  // Get filtered groups based on all criteria
  const filteredGroups = useMemo(() => {
    return enterpriseGroups.filter(group => {
      if (group.id === 'all') {
        return true;
      }

      const { category, municipality, barangay } = getGroupCategory(group);

      // Filter by category
      if (selectedGroupCategory !== 'all' && category !== selectedGroupCategory) {
        return false;
      }

      // Filter by municipality
      if (selectedMunicipality !== 'all' && municipality !== selectedMunicipality) {
        return false;
      }

      // Filter by barangay
      if (selectedBarangay !== 'all' && barangay !== selectedBarangay) {
        return false;
      }

      return true;
    });
  }, [enterpriseGroups, selectedGroupCategory, selectedMunicipality, selectedBarangay]);

  // Fetch enterprise groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setGroupsLoading(true);
        setGroupsError(null);

        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await getAllEnterpriseGroups(token, 1, 1000);
        console.log('Enterprise groups response:', response);
        
        const loadedGroups = response.data?.groups || [];
        console.log('Loaded groups:', loadedGroups);

        // Add "All groups" option at the beginning
        const groupsWithAll = [
          { id: 'all', groupName: 'All groups (All Sub-branches)', createdBy: null } as any,
          ...loadedGroups
        ];

        console.log('Groups with all:', groupsWithAll);
        setEnterpriseGroups(groupsWithAll);

        // Default to "all groups" for enterprise
        setSelectedGroupId('all');
      } catch (e: any) {
        console.error('Error fetching enterprise groups:', e);
        setEnterpriseGroups([]);
        setGroupsError(e?.message || 'Failed to load groups.');
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchGroups();
  }, [navigate]);

  // Fetch overview data
  useEffect(() => {
    if (!selectedGroupId) {
      setOverview(null);
      setDassOverview(null);
      return;
    }

    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        setOverviewError(null);

        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // If a specific group is selected, fetch data for that group only
        if (selectedGroupId && selectedGroupId !== 'all') {
          const yearParam = selectedYear === 'all' ? undefined : selectedYear;
          if (assessmentMode === 'dass21') {
            const response = await getGroupDassOverview(token, selectedGroupId, yearParam);
            setDassOverview(response.data);
            setOverview(null);
          } else if (assessmentMode === 'overall') {
            // Overall Assessment: aggregates individual assessments (mental-health, physical, occupational, etc.)
            const response = await getGroupOverview(token, selectedGroupId, yearParam, 'overall');
            setOverview(response.data);
            setDassOverview(null);
          } else if (assessmentMode === 'sowb') {
            // SOWB: uses StarOfWellBeingAssessment data
            const response = await getGroupOverview(token, selectedGroupId, yearParam, 'sowb');
            setOverview(response.data);
            setDassOverview(null);
          }
        } else {
          // Aggregate data for all enterprise users and filtered groups
          // Use comprehensive endpoint to read ALL user data from enterprise and sub-branches
          const yearParam = selectedYear === 'all' ? undefined : selectedYear;
          
          let dataFetched = false;
          
          // Try to use comprehensive endpoint first
          try {
            const comprehensiveResult = await getComprehensiveEnterpriseOverview(
              token,
              yearParam,
              assessmentMode
            );

            if (comprehensiveResult.success && comprehensiveResult.data) {
              if (assessmentMode === 'dass21') {
                setDassOverview(comprehensiveResult.data as GroupDassOverviewResponse['data']);
                setOverview(null);
              } else if (assessmentMode === 'overall') {
                setOverview(comprehensiveResult.data as GroupOverviewResponse['data']);
                setDassOverview(null);
              } else {
                setOverview(comprehensiveResult.data as GroupOverviewResponse['data']);
                setDassOverview(null);
              }
              dataFetched = true;
            }
          } catch (err) {
            console.warn('Comprehensive endpoint not available, using fallback:', err);
          }

          // Fallback: use group-based aggregation if comprehensive endpoint didn't work
          if (!dataFetched) {
            const realGroups = filteredGroups.filter((g) => g.id !== 'all');
            
            if (realGroups.length === 0) {
              setOverview(null);
              setDassOverview(null);
              return;
            }

            if (assessmentMode === 'dass21') {
              // Fetch DASS-21 for all groups and average-of-averages
              const dassResponses = await Promise.all(
                realGroups.map((g) => getGroupDassOverview(token, g.id, yearParam))
              );

              const validResponses = dassResponses.filter((r) => r.data.counts.withAssessments > 0);
              
              if (validResponses.length === 0) {
                setDassOverview({
                  group: { id: 'all', groupName: 'All groups', createdBy: null },
                  counts: { approvedMembers: 0, withAssessments: 0 },
                  averages: { overall0to42: 0, depression0to42: 0, anxiety0to42: 0, stress0to42: 0 },
                  distribution: { low: 0, moderate: 0, high: 0 }
                });
                setOverview(null);
              } else {
                // Average the averages
                const avgOverall = validResponses.reduce((sum, r) => sum + r.data.averages.overall0to42, 0) / validResponses.length;
                const avgDepression = validResponses.reduce((sum, r) => sum + r.data.averages.depression0to42, 0) / validResponses.length;
                const avgAnxiety = validResponses.reduce((sum, r) => sum + r.data.averages.anxiety0to42, 0) / validResponses.length;
                const avgStress = validResponses.reduce((sum, r) => sum + r.data.averages.stress0to42, 0) / validResponses.length;
                const totalApproved = validResponses.reduce((sum, r) => sum + r.data.counts.approvedMembers, 0);
                const totalWithAssessments = validResponses.reduce((sum, r) => sum + r.data.counts.withAssessments, 0);
                const avgLow = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.low, 0) / validResponses.length);
                const avgModerate = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.moderate, 0) / validResponses.length);
                const avgHigh = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.high, 0) / validResponses.length);

                setDassOverview({
                  group: { id: 'all', groupName: 'All groups', createdBy: null },
                  counts: { approvedMembers: totalApproved, withAssessments: totalWithAssessments },
                  averages: {
                    overall0to42: parseFloat(avgOverall.toFixed(1)),
                    depression0to42: parseFloat(avgDepression.toFixed(1)),
                    anxiety0to42: parseFloat(avgAnxiety.toFixed(1)),
                    stress0to42: parseFloat(avgStress.toFixed(1))
                  },
                  distribution: { low: avgLow, moderate: avgModerate, high: avgHigh }
                });
                setOverview(null);
              }
            } else if (assessmentMode === 'overall') {
              // Fetch Overall Assessment (individual assessments) for all groups and average-of-averages
              const overviewResponses = await Promise.all(
                realGroups.map((g) => getGroupOverview(token, g.id, yearParam, 'overall'))
            );

            const validResponses = overviewResponses.filter((r) => r.data.counts.withAssessments > 0);
            
            if (validResponses.length === 0) {
              setOverview({
                group: { id: 'all', groupName: 'All groups', createdBy: null },
                counts: { approvedMembers: 0, withAssessments: 0 },
                averages: { overall0to4: 0, attributes0to4: {}, attributesPercentage: {} },
                distribution: { low: 0, moderate: 0, high: 0 },
                trends: {}
              });
              setDassOverview(null);
            } else {
              // Calculate overall-of-overall and attributes averages
              const totalApproved = validResponses.reduce((sum, r) => sum + r.data.counts.approvedMembers, 0);
              const totalWithAssessments = validResponses.reduce((sum, r) => sum + r.data.counts.withAssessments, 0);
              
              // Average the overall0to4 values
              const avgOverall0to4 = validResponses.reduce((sum, r) => sum + r.data.averages.overall0to4, 0) / validResponses.length;
              
              // Average each attribute's percentage
              const attributeKeys = ['spiritual', 'nutritional', 'physical', 'emotioPsychoRational', 'legendarial', 'religioTraditioCultural', 'financial', 'environmental', 'socioRelational', 'occupational'];
              const aggregatedAttributes: Record<string, number> = {};
              const aggregatedPercentages: Record<string, number> = {};
              
              attributeKeys.forEach(key => {
                const values = validResponses
                  .map(r => r.data.averages.attributes0to4[key])
                  .filter(v => v !== null && v !== undefined);
                const percentages = validResponses
                  .map(r => r.data.averages.attributesPercentage[key])
                  .filter(p => p !== null && p !== undefined);
                
                aggregatedAttributes[key] = values.length > 0 ? values.reduce((a, b) => a + b) / values.length : 0;
                aggregatedPercentages[key] = percentages.length > 0 ? Math.round(percentages.reduce((a, b) => a + b) / percentages.length) : 0;
              });
              
              // Calculate distribution
              const avgLow = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.low, 0) / validResponses.length);
              const avgModerate = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.moderate, 0) / validResponses.length);
              const avgHigh = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.high, 0) / validResponses.length);

              setOverview({
                group: { id: 'all', groupName: 'All groups', createdBy: null },
                counts: { approvedMembers: totalApproved, withAssessments: totalWithAssessments },
                averages: {
                  overall0to4: avgOverall0to4,
                  attributes0to4: aggregatedAttributes,
                  attributesPercentage: aggregatedPercentages
                },
                distribution: { low: avgLow, moderate: avgModerate, high: avgHigh },
                trends: {}
              });
              setDassOverview(null);
            }
            } else if (assessmentMode === 'sowb') {
              // Fetch SOWB for all groups and average-of-averages
              const overviewResponses = await Promise.all(
                realGroups.map((g) => getGroupOverview(token, g.id, yearParam, 'sowb'))
            );

            const validResponses = overviewResponses.filter((r) => r.data.counts.withAssessments > 0);
            
            if (validResponses.length === 0) {
              setOverview({
                group: { id: 'all', groupName: 'All groups', createdBy: null },
                counts: { approvedMembers: 0, withAssessments: 0 },
                averages: { overall0to4: 0, attributes0to4: {}, attributesPercentage: {} },
                distribution: { low: 0, moderate: 0, high: 0 },
                trends: {}
              });
              setDassOverview(null);
            } else {
              // Average the averages for each attribute
              const attributes0to4: Record<string, number> = {};
              const allKeys = new Set<string>();
              
              validResponses.forEach((r) => {
                Object.keys(r.data.averages.attributes0to4 || {}).forEach((key) => allKeys.add(key));
              });

              allKeys.forEach((key) => {
                const sum = validResponses.reduce((acc, r) => acc + (r.data.averages.attributes0to4?.[key] || 0), 0);
                attributes0to4[key] = parseFloat((sum / validResponses.length).toFixed(2));
              });

              // Calculate attributesPercentage for multi-group case
              const attributesPercentage: Record<string, number> = {};
              const percentageKeys = new Set<string>();

              validResponses.forEach((r) => {
                Object.keys(r.data.averages.attributesPercentage || {}).forEach((key) => percentageKeys.add(key));
              });

              percentageKeys.forEach((key) => {
                const sum = validResponses.reduce((acc, r) => acc + (r.data.averages.attributesPercentage?.[key] || 0), 0);
                attributesPercentage[key] = Math.round(sum / validResponses.length);
              });

              const avgOverall = validResponses.reduce((sum, r) => sum + r.data.averages.overall0to4, 0) / validResponses.length;
              const totalApproved = validResponses.reduce((sum, r) => sum + r.data.counts.approvedMembers, 0);
              const totalWithAssessments = validResponses.reduce((sum, r) => sum + r.data.counts.withAssessments, 0);
              const avgLow = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.low, 0) / validResponses.length);
              const avgModerate = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.moderate, 0) / validResponses.length);
              const avgHigh = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.high, 0) / validResponses.length);

              setOverview({
                group: { id: 'all', groupName: 'All groups', createdBy: null },
                counts: { approvedMembers: totalApproved, withAssessments: totalWithAssessments },
                averages: { overall0to4: parseFloat(avgOverall.toFixed(2)), attributes0to4, attributesPercentage },
                distribution: { low: avgLow, moderate: avgModerate, high: avgHigh },
                trends: {}
              });
              setDassOverview(null);
            }
          }
        }
      }
      } catch (e: any) {
        console.error('Error loading overview:', e);
        setOverview(null);
        setDassOverview(null);
        setOverviewError(e?.message || 'Failed to load overview.');
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
  }, [selectedGroupId, assessmentMode, filteredGroups, selectedYear, navigate]);

  // Define axis for sowbAttributes
  const axis = useMemo(() => [
    { key: 'spiritual', label: 'Spiritual', description: 'Inner peace and purpose alignment', icon: 'self_improvement' },
    { key: 'nutritional', label: 'Nutritional', description: 'Healthy eating practices', icon: 'restaurant' },
    { key: 'physical', label: 'Physical', description: 'Physical fitness and health', icon: 'fitness_center' },
    { key: 'emotioPsychoRational', label: 'Emotio-Psycho-Rational', description: 'Emotional wellbeing and cognitive function', icon: 'psychology' },
    { key: 'legendarial', label: 'Legendarial', description: 'Legacy and life contribution', icon: 'history_edu' },
    { key: 'religioTraditioCultural', label: 'Religio-Traditio-Cultural', description: 'Cultural and religious fulfillment', icon: 'account_balance' },
    { key: 'financial', label: 'Financial', description: 'Financial stability and security', icon: 'payments' },
    { key: 'environmental', label: 'Environmental', description: 'Connection to nature', icon: 'nature' },
    { key: 'socioRelational', label: 'Socio-Relational', description: 'Relationships and social connection', icon: 'diversity_3' },
    { key: 'occupational', label: 'Occupational', description: 'Career satisfaction and growth', icon: 'work' }
  ], []);

  const sowbAttributes: SowbAttribute[] = useMemo(() => {
    if (!overview) return [];

    const display10 = (v0to4: number) => (v0to4 / 4) * 10;

    return axis.map((a) => {
      const v0to4 = overview.averages?.attributes0to4?.[a.key] ?? 0;
      const score10 = display10(v0to4);
      const trend = overview.trends?.[a.key] ?? 'flat';

      let status: 'Good' | 'Fair' | 'Poor';
      if (score10 >= 6.25) {
        status = 'Good';
      } else if (score10 >= 3.75) {
        status = 'Fair';
      } else {
        status = 'Poor';
      }

      return {
        id: a.key,
        name: a.label,
        score: score10 > 0 ? Number(score10.toFixed(1)) : 0,
        trend: trend,
        status,
        description: a.description,
        icon: a.icon
      };
    });
  }, [axis, overview]);

  // DASS-21 Attributes - Depression, Anxiety, Stress (0-42 scale)
  const dassAttributes: SowbAttribute[] = useMemo(() => {
    if (!dassOverview) return [];

    // DASS-21 uses a 0-42 scale for each sub-scale
    // Lower scores are better (unlike SOWB where higher is better)
    // Status thresholds: 0-14 = Good, 15-30 = Fair, 31+ = Poor
    
    const getStatus = (score: number): 'Good' | 'Fair' | 'Poor' => {
      if (score < 15) return 'Good';
      if (score <= 30) return 'Fair';
      return 'Poor';
    };

    const dassScales = [
      {
        id: 'depression',
        name: 'Depression',
        score: dassOverview.averages?.depression0to42 ?? 0,
        icon: 'mood_bad'
      },
      {
        id: 'anxiety',
        name: 'Anxiety',
        score: dassOverview.averages?.anxiety0to42 ?? 0,
        icon: 'warning'
      },
      {
        id: 'stress',
        name: 'Stress',
        score: dassOverview.averages?.stress0to42 ?? 0,
        icon: 'sentiment_very_dissatisfied'
      }
    ];

    return dassScales.map((scale) => {
      const status = getStatus(scale.score);

      return {
        id: scale.id,
        name: scale.name,
        score: scale.score,
        trend: 'flat',
        status,
        description: `${scale.name} level on DASS-21 scale`,
        icon: scale.icon
      };
    });
  }, [dassOverview]);

  const getCategoryColor = (categoryKey: string): string => {
    const colorMap: { [key: string]: string } = {
      spiritual: '#CDB4DB',
      nutritional: '#CDEAC0',
      physical: '#FFD6A5',
      emotioPsychoRational: '#BEE1E6',
      legendarial: '#FFB5C8',
      religioTraditioCultural: '#FFF1C1',
      financial: '#F9E79F',
      environmental: '#D4F1BE',
      socioRelational: '#E4C1F9',
      occupational: '#AEE2E0'
    };
    return colorMap[categoryKey] || '#25b09b';
  };

  // Custom dot component that uses category colors
  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;
    const color = payload.color || '#25b09b';
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill={color}
        stroke={color}
        strokeWidth={2}
      />
    );
  };

  const CustomActiveDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;
    const color = payload.color || '#25b09b';
    return (
      <circle
        cx={cx}
        cy={cy}
        r={8}
        fill={color}
        stroke={color}
        strokeWidth={2}
      />
    );
  };

  const overallChartData = useMemo(() => {
    if (!overview?.averages?.attributesPercentage) return [];
    
    const attributeLabels: { [key: string]: string } = {
      spiritual: 'Spiritual',
      nutritional: 'Nutritional',
      physical: 'Physical',
      emotioPsychoRational: 'Emotio-Psycho-Rational',
      legendarial: 'Legendarial',
      religioTraditioCultural: 'Religio-Traditio-Cultural',
      financial: 'Financial',
      environmental: 'Environmental',
      socioRelational: 'Socio-Relational',
      occupational: 'Occupational'
    };

    return Object.entries(overview.averages.attributesPercentage).map(([key, percentage]) => ({
      key,
      name: attributeLabels[key] || key,
      percentage,
      color: getCategoryColor(key)
    }));
  }, [overview]);

  const distribution = useMemo(() => {
    if (!overview) {
      return { low: 0, moderate: 0, high: 0, dash: { low: 0, moderate: 0, high: 0, offModerate: 0, offHigh: 0 }, avgScore10: 0 };
    }

    const { low, moderate, high } = overview.distribution;
    const total = low + moderate + high;
    const lowPct = total > 0 ? Math.round((low / total) * 100) : 0;
    const moderatePct = total > 0 ? Math.round((moderate / total) * 100) : 0;
    const highPct = total > 0 ? Math.round((high / total) * 100) : 0;

    const circumference = 251.33;
    const lowDash = (lowPct / 100) * circumference;
    const moderateDash = (moderatePct / 100) * circumference;
    const highDash = (highPct / 100) * circumference;
    const offModerate = lowDash;
    const offHigh = lowDash + moderateDash;

    const display10 = (v0to4: number) => (v0to4 / 4) * 10;
    const avgScore10 = display10(overview.averages?.overall0to4 ?? 0);

    return {
      low: lowPct,
      moderate: moderatePct,
      high: highPct,
      dash: { low: lowDash, moderate: moderateDash, high: highDash, offModerate, offHigh },
      avgScore10
    };
  }, [overview]);

  return (
    <div className={`consultant-overview ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-overview__container">
        {/* Header Section */}
        <div className="consultant-overview__header">
          <div className="consultant-overview__header-info">
            <h1 className="consultant-overview__title">
              Enterprise Well-being Overview
            </h1>
          </div>
          <div className="consultant-overview__header-export">
            <button
              className="consultant-overview__export-pdf-btn"
              onClick={handleExportPDF}
              disabled={isPdfExporting || overviewLoading}
              title="Export overview as PDF"
            >
              {isPdfExporting ? (
                <>
                  <span className="material-symbols-outlined consultant-overview__export-pdf-icon">hourglass_top</span>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined consultant-overview__export-pdf-icon">picture_as_pdf</span>
                  <span>Export PDF</span>
                </>
              )}
            </button>
          </div>
          <div className="consultant-overview__header-controls">
            <div className="consultant-overview__controls-row">
              <div className="consultant-overview__control-group consultant-overview__group-selector">
                <label className="consultant-overview__control-label">
                  <span className="material-symbols-outlined">group</span>
                  <span>Group</span>
                </label>
                <div className="consultant-overview__client-selector">
                  <button
                    className={`consultant-overview__dropdown-btn ${selectedGroupId && selectedGroupId !== 'all' ? 'consultant-overview__dropdown-btn--active' : ''}`}
                    onClick={() => setGroupDropdownOpen(!groupDropdownOpen)}
                    disabled={groupsLoading}
                  >
                    <span className="consultant-overview__dropdown-btn-text">
                      {groupsLoading ? 'Loading groups...' : (filteredGroups.find(g => g.id === selectedGroupId)?.groupName || 'All groups (All Sub-branches)')}
                    </span>
                    <span className="consultant-overview__dropdown-icon">
                      <span className="material-symbols-outlined">expand_more</span>
                    </span>
                  </button>
                  {groupDropdownOpen && (
                    <div className="consultant-overview__dropdown-menu">
                      {filteredGroups.map((g) => (
                        <button
                          key={g.id}
                          className={`consultant-overview__dropdown-item ${selectedGroupId === g.id ? 'consultant-overview__dropdown-item--selected' : ''}`}
                          onClick={() => {
                            setSelectedGroupId(g.id);
                            setGroupDropdownOpen(false);
                          }}
                        >
                          {g.groupName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="consultant-overview__control-group consultant-overview__category-selector">
                <label className="consultant-overview__control-label">
                  <span className="material-symbols-outlined">category</span>
                  <span>Group Type</span>
                </label>
                <div className="consultant-overview__client-selector">
                  <button
                    className={`consultant-overview__dropdown-btn ${selectedGroupCategory !== 'all' ? 'consultant-overview__dropdown-btn--active' : ''}`}
                    onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                    disabled={groupsLoading}
                  >
                    <span className="consultant-overview__dropdown-btn-text">
                      {selectedGroupCategory === 'all' ? 'All Types' : 
                       selectedGroupCategory === 'municipality' ? 'Municipality' :
                       selectedGroupCategory === 'barangay' ? 'Barangay' : 'Manually Created'}
                    </span>
                    <span className="consultant-overview__dropdown-icon">
                      <span className="material-symbols-outlined">expand_more</span>
                    </span>
                  </button>
                  {categoryDropdownOpen && (
                    <div className="consultant-overview__dropdown-menu">
                      <button
                        className={`consultant-overview__dropdown-item ${selectedGroupCategory === 'all' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setSelectedGroupCategory('all');
                          setSelectedMunicipality('all');
                          setSelectedBarangay('all');
                          setCategoryDropdownOpen(false);
                        }}
                      >
                        All Types
                      </button>
                      <button
                        className={`consultant-overview__dropdown-item ${selectedGroupCategory === 'municipality' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setSelectedGroupCategory('municipality');
                          setSelectedMunicipality('all');
                          setSelectedBarangay('all');
                          setCategoryDropdownOpen(false);
                        }}
                      >
                        Municipality
                      </button>
                      <button
                        className={`consultant-overview__dropdown-item ${selectedGroupCategory === 'barangay' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setSelectedGroupCategory('barangay');
                          setSelectedMunicipality('all');
                          setSelectedBarangay('all');
                          setCategoryDropdownOpen(false);
                        }}
                      >
                        Barangay
                      </button>
                      <button
                        className={`consultant-overview__dropdown-item ${selectedGroupCategory === 'manual' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setSelectedGroupCategory('manual');
                          setSelectedMunicipality('all');
                          setSelectedBarangay('all');
                          setCategoryDropdownOpen(false);
                        }}
                      >
                        Manually Created
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {selectedGroupCategory !== 'manual' && (selectedGroupCategory === 'all' || selectedGroupCategory === 'municipality' || selectedGroupCategory === 'barangay') && (
                <div className="consultant-overview__control-group consultant-overview__municipality-selector">
                  <label className="consultant-overview__control-label">
                    <span className="material-symbols-outlined">location_city</span>
                    <span>Municipality</span>
                  </label>
                  <div className="consultant-overview__client-selector">
                    <button
                      className={`consultant-overview__dropdown-btn ${selectedMunicipality !== 'all' ? 'consultant-overview__dropdown-btn--active' : ''}`}
                      onClick={() => setMunicipalityDropdownOpen(!municipalityDropdownOpen)}
                      disabled={availableMunicipalities.length === 0}
                    >
                      <span className="consultant-overview__dropdown-btn-text">
                        {selectedMunicipality === 'all' ? 'All Municipalities' : selectedMunicipality}
                      </span>
                      <span className="consultant-overview__dropdown-icon">
                        <span className="material-symbols-outlined">expand_more</span>
                      </span>
                    </button>
                    {municipalityDropdownOpen && (
                      <div className="consultant-overview__dropdown-menu">
                        <button
                          className={`consultant-overview__dropdown-item ${selectedMunicipality === 'all' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                          onClick={() => {
                            setSelectedMunicipality('all');
                            setSelectedBarangay('all');
                            setMunicipalityDropdownOpen(false);
                          }}
                        >
                          All Municipalities
                        </button>
                        {availableMunicipalities.map((municipality) => (
                          <button
                            key={municipality}
                            className={`consultant-overview__dropdown-item ${selectedMunicipality === municipality ? 'consultant-overview__dropdown-item--selected' : ''}`}
                            onClick={() => {
                              setSelectedMunicipality(municipality);
                              setSelectedBarangay('all');
                              setMunicipalityDropdownOpen(false);
                            }}
                          >
                            {municipality}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedGroupCategory !== 'manual' && (selectedGroupCategory === 'barangay' || (selectedGroupCategory === 'all' && selectedMunicipality !== 'all')) && (
                <div className="consultant-overview__control-group consultant-overview__barangay-selector">
                  <label className="consultant-overview__control-label">
                    <span className="material-symbols-outlined">place</span>
                    <span>Barangay</span>
                  </label>
                  <div className="consultant-overview__client-selector">
                    <button
                      className={`consultant-overview__dropdown-btn ${selectedBarangay !== 'all' ? 'consultant-overview__dropdown-btn--active' : ''}`}
                      onClick={() => setBarangayDropdownOpen(!barangayDropdownOpen)}
                      disabled={availableBarangays.length === 0}
                    >
                      <span className="consultant-overview__dropdown-btn-text">
                        {selectedBarangay === 'all' ? 'All Barangays' : selectedBarangay}
                      </span>
                      <span className="consultant-overview__dropdown-icon">
                        <span className="material-symbols-outlined">expand_more</span>
                      </span>
                    </button>
                    {barangayDropdownOpen && (
                      <div className="consultant-overview__dropdown-menu">
                        <button
                          className={`consultant-overview__dropdown-item ${selectedBarangay === 'all' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                          onClick={() => {
                            setSelectedBarangay('all');
                            setBarangayDropdownOpen(false);
                          }}
                        >
                          All Barangays
                        </button>
                        {availableBarangays.map((barangay) => (
                          <button
                            key={barangay}
                            className={`consultant-overview__dropdown-item ${selectedBarangay === barangay ? 'consultant-overview__dropdown-item--selected' : ''}`}
                            onClick={() => {
                              setSelectedBarangay(barangay);
                              setBarangayDropdownOpen(false);
                            }}
                          >
                            {barangay}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="consultant-overview__control-group">
                <label className="consultant-overview__control-label">
                  <span className="material-symbols-outlined">assessment</span>
                  <span>Assessment Type</span>
                </label>
                <div className="consultant-overview__assessment-mode-selector">
                  <button
                    className={`consultant-overview__dropdown-btn ${assessmentMode === 'overall' ? 'consultant-overview__dropdown-btn--active' : ''}`}
                    onClick={() => setAssessmentDropdownOpen(!assessmentDropdownOpen)}
                  >
                    <span className="consultant-overview__dropdown-btn-text">
                      {assessmentMode === 'dass21' ? 'DASS-21' : assessmentMode === 'overall' ? 'Overall Assessment' : 'SOWB'}
                    </span>
                    <span className="consultant-overview__dropdown-icon">
                      <span className="material-symbols-outlined">expand_more</span>
                    </span>
                  </button>
                  {assessmentDropdownOpen && (
                    <div className="consultant-overview__dropdown-menu">
                      <button
                        className={`consultant-overview__dropdown-item ${assessmentMode === 'overall' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setAssessmentMode('overall');
                          setAssessmentDropdownOpen(false);
                        }}
                      >
                        Overall Assessment
                      </button>
                      <button
                        className={`consultant-overview__dropdown-item ${assessmentMode === 'dass21' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setAssessmentMode('dass21');
                          setAssessmentDropdownOpen(false);
                        }}
                      >
                        DASS-21
                      </button>
                      <button
                        className={`consultant-overview__dropdown-item ${assessmentMode === 'sowb' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setAssessmentMode('sowb');
                          setAssessmentDropdownOpen(false);
                        }}
                      >
                        SOWB
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="consultant-overview__control-group">
                <label className="consultant-overview__control-label">
                  <span className="material-symbols-outlined">calendar_month</span>
                  <span>Time Period</span>
                </label>
                <div className="consultant-overview__year-selector">
                  <button 
                    className="consultant-overview__period-btn"
                    onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
                  >
                    <span>{selectedYear === 'all' ? 'All Time' : selectedYear}</span>
                    <span className="material-symbols-outlined">expand_more</span>
                  </button>
                  {yearDropdownOpen && (
                    <div className="consultant-overview__year-dropdown-menu">
                      <button
                        className={`consultant-overview__dropdown-item ${selectedYear === 'all' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setSelectedYear('all');
                          setYearDropdownOpen(false);
                        }}
                      >
                        All Time
                      </button>
                      {[2024, 2025, 2026, 2027].map((year) => (
                        <button
                          key={year}
                          className={`consultant-overview__dropdown-item ${selectedYear === year ? 'consultant-overview__dropdown-item--selected' : ''}`}
                          onClick={() => {
                            setSelectedYear(year);
                            setYearDropdownOpen(false);
                          }}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={pdfContentRef}>
        {!groupsLoading && enterpriseGroups.length === 0 ? (
          <div className="consultant-overview__empty-state">
            <div className="consultant-overview__card">
              <div className="consultant-overview__empty-state-content">
                <h3 className="consultant-overview__card-title">No groups created yet</h3>
                <p className="consultant-overview__card-subtitle">
                  Create groups under your enterprise to view well-being data across all sub-branches.
                </p>
              </div>
              {groupsError && (
                <p className="consultant-overview__empty-state-error" style={{ color: '#ef4444', marginTop: '1rem' }}>
                  Error: {groupsError}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {overviewLoading && (
              <div className="consultant-overview__status-row">
                <span className="consultant-overview__status-text">Loading overview...</span>
              </div>
            )}
            {overviewError && (
              <div className="consultant-overview__status-row">
                <span className="consultant-overview__status-text consultant-overview__status-text--error">{overviewError}</span>
              </div>
            )}

            {/* Main Grid Layout */}
            <div className="consultant-overview__grid">
              {/* Line Chart Section - Overall Assessment */}
              {assessmentMode === 'overall' && (
                <div className="consultant-overview__radar-section">
                  <div className="consultant-overview__card">
                    <div className="consultant-overview__card-header">
                      <div>
                        <h3 className="consultant-overview__card-title">Overall Assessment Graph</h3>
                        <p className="consultant-overview__card-subtitle">Population Assessment across 10 dimensions</p>
                      </div>
                    </div>
                    {overview && overview.counts?.approvedMembers > 0 && overview.counts?.withAssessments === 0 && (
                      <div className="consultant-overview__inline-empty">
                        No assessments found yet for approved members across all groups.
                      </div>
                    )}
                    {overview && overview.counts?.approvedMembers === 0 && (
                      <div className="consultant-overview__inline-empty">
                        No approved members across all groups yet.
                      </div>
                    )}
                    {overview && overview.counts?.withAssessments > 0 && (
                      <>
                        {overallChartData.length > 0 ? (
                          <div className="consultant-overview__line-chart-container">
                            <div className="consultant-overview__chart-info" style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e1' : '#888', marginBottom: '1rem', paddingLeft: '1rem' }}>
                              <strong>Data Period:</strong> {selectedYear === 'all' ? 'All Time' : `${selectedYear}`} • <strong>Assessments:</strong> {overview.counts?.withAssessments} member{overview.counts?.withAssessments !== 1 ? 's' : ''}
                            </div>
                            <ResponsiveContainer width="100%" height={400}>
                              <LineChart data={overallChartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#475569' : '#d1d5db'} />
                                <XAxis
                                  dataKey="name"
                                  angle={-45}
                                  textAnchor="end"
                                  height={100}
                                  fontSize={12}
                                  stroke={isDarkMode ? '#94a3b8' : '#374151'}
                                  tick={{ fill: isDarkMode ? '#cbd5e1' : '#374151' }}
                                />
                                <YAxis
                                  domain={[0, 100]}
                                  tickFormatter={(value) => `${value}%`}
                                  fontSize={12}
                                  stroke={isDarkMode ? '#94a3b8' : '#374151'}
                                  tick={{ fill: isDarkMode ? '#cbd5e1' : '#374151' }}
                                />
                                <Tooltip
                                  formatter={(value) => `${value}%`}
                                  labelFormatter={(label) => `Category: ${label}`}
                                  contentStyle={{
                                    backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                                    border: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}`,
                                    borderRadius: '8px',
                                    boxShadow: isDarkMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    color: isDarkMode ? '#cbd5e1' : '#1f2937'
                                  }}
                                  labelStyle={{ color: isDarkMode ? '#cbd5e1' : '#1f2937' }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="percentage"
                                  stroke="#25b09b"
                                  strokeWidth={3}
                                  dot={<CustomDot />}
                                  activeDot={<CustomActiveDot />}
                                />
                              </LineChart>
                            </ResponsiveContainer>

                            {/* Individual Category Percentages - Attribute Cards */}
                            <div className="consultant-overview__chart-percentages">
                              <h4 className="consultant-overview__percentages-title">Category Breakdown (%)</h4>
                              <div className="consultant-overview__category-attributes">
                                {overallChartData.map((item, index) => {
                                  // Find axis entry for icon
                                  const axisEntry = axis.find(a => a.key === item.key);
                                  // Determine status based on category-specific thresholds
                                  let statusClass: string;
                                  let statusLabel: string;
                                  
                                  if (item.key === 'financial') {
                                    // Financial has its own thresholds
                                    statusClass = item.percentage >= 70 ? 'high' : item.percentage >= 40 ? 'moderate' : 'low';
                                    statusLabel = item.percentage >= 70 ? 'High' : item.percentage >= 40 ? 'Moderate' : 'Low';
                                  } else {
                                    // All other categories use standard thresholds
                                    statusClass = item.percentage >= 75 ? 'high' : item.percentage >= 50 ? 'moderate' : 'low';
                                    statusLabel = item.percentage >= 75 ? 'High' : item.percentage >= 50 ? 'Moderate' : 'Low';
                                  }
                                  
                                  // Emotio-Psycho-Rational and Occupational have inverted color logic
                                  const isInvertedCategory = item.key === 'occupational' || item.key === 'emotioPsychoRational';
                                  const statusClassWithModifier = isInvertedCategory 
                                    ? `consultant-overview__category-status--${statusClass} consultant-overview__category-status--inverted`
                                    : `consultant-overview__category-status--${statusClass}`;
                                  
                                  return (
                                    <div key={index} className="consultant-overview__category-card">
                                      <div className="consultant-overview__category-card-top">
                                        <div className={`consultant-overview__category-icon-wrapper consultant-overview__category-icon-wrapper--${item.key}`}>
                                          <span className="material-symbols-outlined">{axisEntry?.icon || 'star'}</span>
                                        </div>
                                        <h4 className="consultant-overview__category-name">{item.name}</h4>
                                      </div>
                                      <div className="consultant-overview__category-card-bottom">
                                        {item.percentage !== null && item.percentage !== undefined ? (
                                          <>
                                            <span className="consultant-overview__category-percentage">{item.percentage}%</span>
                                            <span className={`consultant-overview__category-status ${statusClassWithModifier}`}>
                                              {statusLabel}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="consultant-overview__category-no-data">No assessment taken</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="consultant-overview__inline-empty">
                            Unable to load assessment data. Please ensure members have completed assessments.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* DASS-21 Section - For DASS-21 Mode */}
              {assessmentMode === 'dass21' && (
                <div className="consultant-overview__dass-section">
                  <div className="consultant-overview__card">
                    <div className="consultant-overview__card-header">
                      <div>
                        <h3 className="consultant-overview__card-title">DASS-21 Assessment Breakdown</h3>
                        <p className="consultant-overview__card-subtitle">Depression, Anxiety, Stress Scales (0-42)</p>
                      </div>
                    </div>
                    {dassOverview && dassOverview.counts?.withAssessments > 0 ? (
                      <div className="consultant-overview__dass-metrics">
                        {dassAttributes.map((attr) => (
                          <div key={attr.id} className="consultant-overview__dass-metric-card">
                            <div className="consultant-overview__dass-metric-header">
                              <span className="material-symbols-outlined">{attr.icon}</span>
                              <h4 className="consultant-overview__dass-metric-name">{attr.name}</h4>
                            </div>
                            <div className="consultant-overview__dass-metric-body">
                              <div className={`consultant-overview__dass-metric-score consultant-overview__dass-metric-score--${attr.status.toLowerCase()}`}>
                                {attr.score.toFixed(1)}
                              </div>
                              <div className="consultant-overview__dass-metric-scale">
                                <span>0</span>
                                <span>21</span>
                                <span>42</span>
                              </div>
                              <span className={`consultant-overview__dass-metric-status consultant-overview__dass-metric-status--${attr.status.toLowerCase()}`}>
                                {attr.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="consultant-overview__inline-empty">
                        {dassOverview?.counts?.approvedMembers === 0
                          ? 'No approved members across all groups yet.'
                          : 'No DASS-21 assessments found yet for approved members.'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* SOWB Section - For SOWB Mode */}
              {assessmentMode === 'sowb' && (
                <div className="consultant-overview__sowb-section">
                  <div className="consultant-overview__card">
                    <div className="consultant-overview__card-header">
                      <div>
                        <h3 className="consultant-overview__card-title">Star of Well-Being Analysis</h3>
                        <p className="consultant-overview__card-subtitle">10-Dimensional Assessment across All Members</p>
                      </div>
                    </div>
                    {overview && overview.counts?.withAssessments > 0 ? (
                      <div className="consultant-overview__sowb-content">
                        {/* Chart Section */}
                        <div className="consultant-overview__sowb-chart">
                          <StarRadarChart
                            scores={overview.averages?.attributes0to4 || {}}
                            chartMode="filled"
                            size="full"
                            attributeLabels={{
                              spiritual: 'Spiritual',
                              nutritional: 'Nutritional',
                              physical: 'Physical',
                              emotioPsychoRational: 'Emotio-Psycho-Rational',
                              legendarial: 'Legendarial',
                              religioTraditioCultural: 'Religio-Traditio-Cultural',
                              financial: 'Financial',
                              environmental: 'Environmental',
                              socioRelational: 'Socio-Relational',
                              occupational: 'Occupational'
                            }}
                          />
                        </div>

                        {/* Attributes Grid */}
                        <div className="consultant-overview__sowb-attributes">
                          {(() => {
                            const attributeNames: { [key: string]: string } = {
                              spiritual: 'Spiritual',
                              nutritional: 'Nutritional',
                              physical: 'Physical',
                              emotioPsychoRational: 'Emotio-Psycho-Rational',
                              legendarial: 'Legendarial',
                              religioTraditioCultural: 'Religio-Traditio-Cultural',
                              financial: 'Financial',
                              environmental: 'Environmental',
                              socioRelational: 'Socio-Relational',
                              occupational: 'Occupational'
                            };

                            const getAttributeIcon = (key: string): string => {
                              const icons: { [key: string]: string } = {
                                spiritual: 'self_improvement',
                                nutritional: 'restaurant',
                                physical: 'fitness_center',
                                emotioPsychoRational: 'psychology',
                                legendarial: 'history_edu',
                                religioTraditioCultural: 'account_balance',
                                financial: 'payments',
                                environmental: 'nature',
                                socioRelational: 'diversity_3',
                                occupational: 'work'
                              };
                              return icons[key] || 'star';
                            };

                            const attributeOrder = [
                              'spiritual', 'nutritional', 'physical', 'emotioPsychoRational', 'legendarial',
                              'religioTraditioCultural', 'financial', 'environmental', 'socioRelational', 'occupational'
                            ];

                            return attributeOrder.map((key) => {
                              const score = overview.averages?.attributes0to4?.[key] ?? 0;
                              const roundedScore = Math.round(score);
                              const statusClass = roundedScore >= 4 ? 'excellent' : roundedScore >= 3 ? 'good' : roundedScore >= 2 ? 'fair' : 'poor';
                              const statusLabel = roundedScore >= 4 ? 'Excellent' : roundedScore >= 3 ? 'Good' : roundedScore >= 2 ? 'Fair' : 'Poor';

                              return (
                                <div key={key} className="consultant-overview__sowb-attribute-card">
                                  <div className="consultant-overview__sowb-attribute-top">
                                    <div className={`consultant-overview__sowb-attribute-icon-wrapper consultant-overview__sowb-attribute-icon-wrapper--${key}`}>
                                      <span className="material-symbols-outlined">{getAttributeIcon(key)}</span>
                                    </div>
                                    <h4 className="consultant-overview__sowb-attribute-name">{attributeNames[key]}</h4>
                                  </div>
                                  <div className="consultant-overview__sowb-attribute-bottom">
                                    <span className="consultant-overview__sowb-attribute-score">{roundedScore}/4</span>
                                    <span className={`consultant-overview__sowb-attribute-status consultant-overview__sowb-attribute-status--${statusClass}`}>
                                      {statusLabel}
                                    </span>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="consultant-overview__inline-empty">
                        {overview?.counts?.approvedMembers === 0
                          ? 'No approved members across all groups yet.'
                          : 'No SOWB assessments found yet for members.'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Right Column - Distribution Only for Overall Assessment Mode */}
              {assessmentMode === 'overall' && overview && (
                <div className="consultant-overview__right-section">
                  {/* Distribution Chart */}
                  <div className="consultant-overview__distribution-section">
                    <div className="consultant-overview__card">
                      <div className="consultant-overview__card-header">
                        <div>
                          <h3 className="consultant-overview__card-title">Well-being Distribution</h3>
                          <p className="consultant-overview__card-subtitle">High / Moderate / Low</p>
                        </div>
                        <div className="consultant-overview__sample-size">
                          N = {overview?.counts?.withAssessments ?? '–'}
                        </div>
                      </div>
                      <div className="consultant-overview__distribution-content">
                        <div className="consultant-overview__donut-chart">
                          <svg className="consultant-overview__donut-svg" viewBox="0 0 100 100">
                            <circle className="consultant-overview__donut-bg" cx="50" cy="50" fill="transparent" r="40" strokeWidth="12"></circle>
                            <circle
                              className="consultant-overview__donut-segment consultant-overview__donut-segment--low"
                              cx="50"
                              cy="50"
                              fill="transparent"
                              r="40"
                              strokeDasharray={`${distribution.dash.low} 251`}
                              strokeWidth="12"
                            ></circle>
                            <circle
                              className="consultant-overview__donut-segment consultant-overview__donut-segment--moderate"
                              cx="50"
                              cy="50"
                              fill="transparent"
                              r="40"
                              strokeDasharray={`${distribution.dash.moderate} 251`}
                              strokeDashoffset={distribution.dash.offModerate}
                              strokeWidth="12"
                            ></circle>
                            <circle
                              className="consultant-overview__donut-segment consultant-overview__donut-segment--high"
                              cx="50"
                              cy="50"
                              fill="transparent"
                              r="40"
                              strokeDasharray={`${distribution.dash.high} 251`}
                              strokeDashoffset={distribution.dash.offHigh}
                              strokeWidth="12"
                            ></circle>
                          </svg>
                          <div className="consultant-overview__donut-center">
                            <span className="consultant-overview__donut-value">
                              {distribution.avgScore10 > 0 ? distribution.avgScore10.toFixed(1) : '–'}
                            </span>
                            <span className="consultant-overview__donut-label">Avg Score</span>
                          </div>
                        </div>
                        <div className="consultant-overview__distribution-legend">
                          <div className="consultant-overview__distribution-item">
                            <div className="consultant-overview__distribution-indicator">
                              <span className="consultant-overview__distribution-dot consultant-overview__distribution-dot--high"></span>
                              <span className="consultant-overview__distribution-label">High</span>
                            </div>
                            <span className="consultant-overview__distribution-percentage">{distribution.high}%</span>
                          </div>
                          <div className="consultant-overview__distribution-item">
                            <div className="consultant-overview__distribution-indicator">
                              <span className="consultant-overview__distribution-dot consultant-overview__distribution-dot--moderate"></span>
                              <span className="consultant-overview__distribution-label">Moderate</span>
                            </div>
                            <span className="consultant-overview__distribution-percentage">{distribution.moderate}%</span>
                          </div>
                          <div className="consultant-overview__distribution-item">
                            <div className="consultant-overview__distribution-indicator">
                              <span className="consultant-overview__distribution-dot consultant-overview__distribution-dot--low"></span>
                              <span className="consultant-overview__distribution-label">Low</span>
                            </div>
                            <span className="consultant-overview__distribution-percentage">{distribution.low}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
        </div>{/* end pdfContentRef */}
    </div>
  );
};

export default EnterpriseOverviewPage;

